import { inject, Injectable } from '@angular/core';
import { Action, createAction, createReducer, createSelector, on, props, Store } from '@ngrx/store';
import { AppState } from '@ra-state';
import { filter } from 'rxjs';
import { SnackBarData } from './domain';

export type UiState = {
  isLoading: number;
  showArchiveBanner: boolean;
  snackBarData?: SnackBarData;
};

// actions
const loading = createAction('[UI State] Set Loading', props<{ value: boolean }>());
const showTenantArchiveBanner = createAction('[UI State] Show Tenant Archive Banner', props<{ show: boolean }>());
const displayMessage = createAction('[UI State] Snackbar Message', props<{ payload: SnackBarData }>());

export const UiStateActions = {
  loading,
  showTenantArchiveBanner,
  displayMessage,
};

export const initialUiState: UiState = {
  isLoading: 0,
  showArchiveBanner: false,
};

// selectors
const selectUiState = (state: AppState): UiState => state.uiState ?? initialUiState;
export const selectLoadingState = createSelector(selectUiState, (state: UiState) => state.isLoading > 0);
export const selectShowArchiveBanner = createSelector(selectUiState, (state: UiState) => state.showArchiveBanner);
export const selectDisplayMessage = createSelector(selectUiState, (state: UiState) => state.snackBarData);

// reducer
const uiStateImpl = createReducer(
  initialUiState,
  on(loading, (state: UiState, { value }): UiState => {
    const delta = value ? 1 : -1;
    const isLoading = state.isLoading + delta;
    if (isLoading < 0) {
      // eslint-disable-next-line no-console
      console.warn('reducer: isLoading is going negative!!!');
    }
    return { ...state, isLoading: isLoading };
  }),
  on(
    showTenantArchiveBanner,
    (state: UiState, { show }): UiState => ({
      ...state,
      showArchiveBanner: show,
    }),
  ),
  on(
    displayMessage,
    (state: UiState, { payload }): UiState => ({
      ...state,
      snackBarData: payload,
    }),
  ),
);

export function uiStateReducer(state: UiState, action: Action): UiState {
  return uiStateImpl(state, action);
}

// facade
@Injectable({
  providedIn: 'root',
})
export class UiStateFacade {
  private readonly store$ = inject<Store<AppState>>(Store);

  isLoading$ = this.store$.select(selectLoadingState);
  // move to UI state (peer of snackbar, dialog, modal etc) and used commonly to display all the diff banners
  // maintwindow, sessiontimoeout, token exhaustion, archival etc
  showArchiveBanner$ = this.store$.select(selectShowArchiveBanner);

  displayMessage$ = this.store$.select(selectDisplayMessage).pipe(filter((message) => message !== undefined));

  setLoading(value: boolean): void {
    this.store$.dispatch(loading({ value }));
  }

  displayMessage(messageData: SnackBarData): void {
    this.store$.dispatch(displayMessage({ payload: messageData }));
  }
}

// effects
@Injectable({
  providedIn: 'root',
})
export class UiStateEffects { }
